import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { PriceListRatioRowData, WorkOrderChangePriceItem } from 'gql/graphql';
import { AddRatioToActKc2StrategyEnum, RatioTypeEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { AddCoefficientTabsTypes } from 'pages/Project/UerProject/AddCoefficientTabsTypes';
import { CreateOrEditRatio } from 'pages/Project/UerProject/Ratio/CreateOrEditRatio/CreateOrEditRatio';
import { getRowClass, listCheckedIds } from 'pages/Project/UerProject/WorkOrder/AddCoefficient';
import { agGridAdapter, cnMixSpace, Loader } from 'uikit';

import type React from 'react';
import { useCallback, useMemo } from 'react';

import { AddRatioButton } from '../../WorkOrder/AddCoefficient/components/AddRatioButton';
import { RatioRowsWithScopesSelectorModal } from '../../WorkOrder/AddCoefficient/components/RatioRowsWithScopesSelectorModal';
import { useAddItemsToWorkOrderChange, useAddRatioToWorkOrderChangeTableData } from '../hooks';
import { addRatioToWorkOrderChange } from '../WorkOrderChangeQueries';

import { useColumnsWorkOrderChangeAddCoefficient } from './useColumnsWorkOrderChangeAddCoefficient';

interface IAddRatioToWorkOrderChange {
    isOpened: boolean;
    closeModalHandler: () => void;
    resetCheckedRows: () => void;
    refreshTable: (objectIds: number[]) => void;
    checkedUerRow: WorkOrderChangePriceItem;
    strategy: AddRatioToActKc2StrategyEnum | true;
    workOrderChangeUerRules: IUseAccess['workOrderChangeUer'];
    contractId: number;
}

const styleOptions = agGridAdapter({
    size: 'm',
    borderBetweenColumns: false,
    borderBetweenRows: true,
});

const getRowId = ({ data }: { data: PriceListRatioRowData }) => data?.id;

export const AddRatioToWorkOrderChange: React.FC<IAddRatioToWorkOrderChange> = (props) => {
    const {
        isOpened,
        closeModalHandler,
        resetCheckedRows,
        refreshTable,
        checkedUerRow,
        strategy,
        workOrderChangeUerRules,
        contractId,
    } = props;

    const modalDialogTitle = useMemo(
        () => `Добавление коэффициента к расценке ${checkedUerRow?.code ?? ''}`,
        [checkedUerRow?.code],
    );

    const { workOrderChangePriceId, workOrderPriceId } = useMemo(
        () => ({
            workOrderChangePriceId: checkedUerRow?.workOrderChangePriceId,
            workOrderPriceId: checkedUerRow?.workOrderPriceId,
        }),
        [checkedUerRow],
    );

    const objectIdsArrAddMutation = useMemo(
        () => [checkedUerRow?.workOrderObjectId],
        [checkedUerRow?.workOrderObjectId],
    );

    const onSuccessAddHandler = useCallback(() => {
        refreshTable(objectIdsArrAddMutation);
        resetCheckedRows();
        closeModalHandler();
    }, [closeModalHandler, objectIdsArrAddMutation, refreshTable, resetCheckedRows]);

    const {
        tableRows,
        getRatioStatus,
        setActiveTabId,
        checkedItemsCounter,
        checkedRatios,
        changeFilterHandler,
        activeTabId,
        isCoefficientCreating,
        isCoefficientSelecting,
        onCreateCoefficientVisibilityChange,
        onSelectCoefficientVisibilityChange,
        onCoefficientTypeChange,
        onCreateCoefficientConfirm,
        onCoefficientSelectingConfirm,
        valueNote,
        handleChangesValueNote,
        handleChangeCheckbox,
        handleAllChangeCheckbox,
    } = useAddRatioToWorkOrderChangeTableData({
        isOpened,
        workOrderChangePriceId,
        workOrderPriceId,
    });

    const currentStrategy = useMemo(() => {
        if (strategy === AddRatioToActKc2StrategyEnum.CorrectOriginal) {
            return AddRatioToActKc2StrategyEnum.CorrectOriginal;
        }

        if (strategy === AddRatioToActKc2StrategyEnum.DuplicateWithExtendedCoefficients) {
            return AddRatioToActKc2StrategyEnum.DuplicateWithExtendedCoefficients;
        }

        return null;
    }, [strategy]);

    const ratioRowIds = useMemo(() => listCheckedIds(checkedRatios) as number[], [checkedRatios]);

    const ratioInputs = useMemo(() => {
        if (Array.isArray(ratioRowIds)) {
            return ratioRowIds.map((ratioRowId) => ({
                ratioRowId,
                rationale: valueNote[ratioRowId] || '',
            }));
        }

        return [];
    }, [ratioRowIds, valueNote]);

    const variablesForAdding = useMemo(
        () => ({
            workOrderChangePriceId,
            workOrderPriceId,
            strategy: currentStrategy,
            ratioInputs,
        }),
        [workOrderChangePriceId, workOrderPriceId, currentStrategy, ratioInputs],
    );

    const {
        addToWorkOrderChangeHandler,
        addResponse,
        addStatus,
        addErr,
        addIsSubmitted,
        closeErrorModal,
        handleCreateReport,
    } = useAddItemsToWorkOrderChange({
        query: addRatioToWorkOrderChange,
        requestId: 'uerAddRatioToWorkOrderChange',
        variables: variablesForAdding,
        onSuccessAddHandler,
    });

    const isConfirmBtnDisabled = !ratioRowIds.length;

    const updateGroupRowData = useMemo(() => {
        if (!tableRows || !Array.isArray(tableRows)) {
            return [];
        }

        const hasNewObj = tableRows.some((item) => item);

        if (hasNewObj && activeTabId !== RatioTypeEnum.TechPartCoef) {
            const rowGroup = {
                id: tableRows[0].groupId,
                workConditions: tableRows[0].groupName,
                ratioType: tableRows[0].ratioType,
                groupType: tableRows[0].groupType,
                groupRow: true,
            };

            return [rowGroup, ...tableRows];
        }

        return tableRows;
    }, [activeTabId, tableRows]);

    const { finalColumns } = useColumnsWorkOrderChangeAddCoefficient({
        data: tableRows,
        checkedIds: checkedRatios,
        activeTabId,
        changeFilterHandler,
        handleChangesValueNote,
        valueNote,
        handleChangeCheckbox,
        handleAllChangeCheckbox,
        workOrderChangePriceId,
        workOrderPriceId,
    });

    const isLoading = getRatioStatus === RequestStatus.FETCHING;

    return (
        <ModalDialog
            title={modalDialogTitle}
            isOpen={isOpened}
            hasOverlay
            withTitleCloseButton
            onClose={closeModalHandler}
            onConfirm={addToWorkOrderChangeHandler}
            btnConfirmDisabled={isConfirmBtnDisabled}
            btnConfirmLabel="Добавить в Изм. к НЗ"
            btnCancelLabel="Отменить"
            className="modalDialogAddCoefficient inProgress"
            leftPanel={
                <AddRatioButton
                    ratioType={activeTabId}
                    isCreateDisabled={!workOrderChangeUerRules.canManualCreation}
                    onSelectCoefficientVisibilityChange={onSelectCoefficientVisibilityChange}
                    onCreateCoefficientVisibilityChange={onCreateCoefficientVisibilityChange}
                    groupType={updateGroupRowData?.[0]?.groupType}
                />
            }
        >
            <AddCoefficientTabsTypes
                onChangeActiveTab={setActiveTabId}
                countItems={checkedItemsCounter}
                className={cnMixSpace({ p: 'xl' })}
            />
            <div id="addCoefficient" className="ag-theme-alpine AddCoefficient">
                {isLoading ? (
                    <Loader />
                ) : (
                    <AgGridTable
                        {...styleOptions}
                        getRowId={getRowId}
                        rowData={updateGroupRowData as PriceListRatioRowData[]}
                        columnDefs={finalColumns}
                        rowClassRules={getRowClass}
                        suppressCellFocus
                        rowSelection="multiple"
                        rowHeight={40}
                        context={{
                            tableUniqueId: `AddRatioToWorkOrderChangeUer_${activeTabId}`,
                        }}
                    />
                )}
            </div>
            <ErrorHandlingModal
                titleText={modalDialogTitle}
                isSubmitted={addIsSubmitted}
                statusRequest={addStatus}
                dataRequest={addResponse?.uerAddRatioToWorkOrderChange}
                errorsRequest={addErr}
                handleCloseModalError={closeErrorModal}
                handleCreateReport={handleCreateReport}
            />
            {isCoefficientCreating && (
                <CreateOrEditRatio
                    ratioType={activeTabId}
                    onClose={onCreateCoefficientVisibilityChange}
                    onConfirm={onCreateCoefficientConfirm}
                    onRatioTypeChange={onCoefficientTypeChange}
                    uer={checkedUerRow}
                    groupType={updateGroupRowData?.[0]?.groupType}
                />
            )}
            {isCoefficientSelecting && (
                <RatioRowsWithScopesSelectorModal
                    onClose={onCoefficientSelectingConfirm}
                    uerBaseRowId={checkedUerRow.entryId}
                    ratioType={activeTabId}
                    contractId={contractId}
                />
            )}
        </ModalDialog>
    );
};

AddRatioToWorkOrderChange.displayName = 'AddRatioToWorkOrderChange';
